import { createContext, ReactElement, ReactNode, useContext, useState } from "react";
import i18next, { Resource, ResourceKey } from "i18next";
import { merge } from "lodash";
import Markdown from "markdown-to-jsx";
import React from "react";
import { LanguageContext } from "./LanguageProvider";
import { useLogger } from "./LogProvider";

interface TransFunction {
	(keys: string | string[], options?: { lng?: string; ns?: string; defaultValue?: any; replace?: boolean });
}

interface TransInterface {
	id?: string;
	lang: string;
	lookup: TransFunction;
}

export const TransContext = createContext<TransInterface>({
	id: "root",
	lang: "en",
	lookup: (key: string, options?: any) => (options?.defaultValue === undefined ? key : options?.defaultValue)
});
TransContext.displayName = "Trans";

const SimpleContainer = (props: {
	/**
	 * identifier for this container, used for debugging
	 */
	id?: string;
	/**
	 * enables debug messages
	 */
	debug?: boolean;
	/**
	 * default language
	 */
	lang?: string;
	/**
	 * default namespace for tokens
	 */
	ns?: string;
	/**
	 * language bundles
	 */
	resources: Resource[] | Resource;
	/**
	 * children catch-all
	 */
	children?: ReactNode;
}) => {
	const LangCtx = useContext(LanguageContext);
	const parent = useContext(TransContext);
	const logger = useLogger("providers.TranslationProvider");
	const [next] = useState(() => {
		/**
		 * Merge all of the dictionaries so we can use them
		 *
		 * TODO switch merge to non-overwrite merge routine.
		 * Right now, the right hand dictionaries will override the left side when merging.
		 */
		let res = props.resources;
		if (Array.isArray(props.resources)) {
			res = props.resources[0];
			for (let i = 1; i < props.resources.length; i++) {
				res = merge(res, props.resources[i]);
			}
		}

		/**
		 * We need to ensure the fallbackLng is a valid language,
		 * or else i18next will break.
		 *
		 * Looking for "en" | "es" only for now.
		 */
		const fallbackLng = /(^en$|^es$)+/.test(parent.lang) ? parent.lang : "en";

		/**
		 * Since this is the default instance in useState, this will only be executed once.
		 */

		return i18next.createInstance(
			{
				debug: props.debug,
				lng: props.lang ?? LangCtx.language ?? fallbackLng,
				resources: res as Resource,
				fallbackLng,
				lowerCaseLng: true,
				initImmediate: false,
				returnNull: true
			},
			(err) => {
				if (err) return logger.warn("i18next createInstance", "unable to load bundles, i18next", err);
			}
		);
	});
	const search = (key: string, options?: any): string => {
		return (
			next.t(
				key,
				merge({ lng: props.lang ?? LangCtx.language ?? parent?.lang, ns: props.ns, defaultValue: null }, options)
			) ??
			parent?.lookup(
				key,
				merge({ lng: props.lang ?? LangCtx.language ?? parent?.lang, replace: false, defaultValue: null }, options)
			)
		);
	};
	const lookup = (key: string, options?: any): string => {
		let result = search(key, options) ?? (options?.defaultValue === undefined ? key : options?.defaultValue);
		if (result && options?.replace != false) {
			result = result.replace(/{{(.+?)}}/g, (str, tag) => {
				return search(tag, options) ?? tag;
			});
		}
		return result;
	};

	return (
		<TransContext.Provider
			value={{ id: props.id, lang: props.lang ?? LangCtx.language ?? parent.lang, lookup: lookup }}
		>
			{props.children}
		</TransContext.Provider>
	);
};

SimpleContainer.displayName = "Trans.Container";

interface TransFragmentProps {
	id?: string;
	ns?: string;
	properties?: ResourceKey;
	markdown?: boolean;
	children?: string;
	debug?: boolean;
}

const TransFragment = (props: TransFragmentProps): ReactElement => {
	const context = useContext(TransContext);
	const logger = useLogger("providers.TransFragment");
	const replace = (key: string) => {
		const parts = key.split(/{{|}}/);
		return (
			<>
				{parts
					.filter((part) => part !== "")
					.map((part, index) =>
						props.markdown ? (
							<React.Fragment key={part + index}>
								{props.properties?.[part] ?? (
									<Markdown>{context?.lookup(part, { ns: props.ns, replace: true }) ?? part}</Markdown>
								)}
							</React.Fragment>
						) : (
							<React.Fragment key={part + index}>
								{props.properties?.[part] ?? context?.lookup(part, { ns: props.ns, replace: true }) ?? part}
							</React.Fragment>
						)
					)}
			</>
		);
	};
	const render = () => {
		/**
		 * If properties are set, we need to disable the upstream interpolation to prevent
		 * embedded key replacement.  Unlike the standard translation routines, this component
		 * can construct React elements from an input string to render advanced stuff in the UI.
		 */
		let target: any = props.id ?? props.children;
		let translation: any = context?.lookup(target, {
			ns: props.ns,
			defaultValue: null,
			replace: false
		});
		if (props.debug) {
			if (!translation) {
				logger.info("render", `trans.late missing key "${target}"`);
			} else {
				logger.info("render", `trans.late found key "${target}" translation "${translation}"`);
			}
		}
		return replace(translation ?? target);
	};
	return render();
};
TransFragment.displayName = "Trans.Late";

export class Trans {
	public static readonly context = TransContext;
	public static readonly container = SimpleContainer;
	public static readonly consumer = TransContext.Consumer;
	public static readonly late = TransFragment;
}
